import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useWorkspace } from "../contexts/WorkspaceContext";
import { useAuth } from "../contexts/AuthContext";
import Topbar from "../components/Topbar";
import {
  Lock, Save, Pencil, Check, AlertTriangle, RotateCcw, Sparkles,
  Upload, X,
} from "lucide-react";
import MonthlyTargetsCard, { getMonthlyTargetKeys } from "../components/MonthlyTargetsCard";
import OutletDemandCalendar from "../components/OutletDemandCalendar";
import OpportunityDashboard from "../components/OpportunityDashboard";

// --- Field schema -------------------------------------------------------
// Each card has a header label and N sub-fields. Stored in
// db.fnb_profiles.{ws}.data with flat URL-safe keys following the
// `<column>_<header>_<subfield>` shape. Allowed prefixes (aesthetics_, ops_,
// biz_, swot_) and exact `critical_issues` are preserved so the migration
// allow-list keeps purging legacy keys cleanly.

const AESTHETICS = [
  { header: "Concept",  fields: [
    { key: "aesthetics_concept_description",   label: "Description" },
    { key: "aesthetics_concept_image_url",     label: "Restaurant image" },
    { key: "aesthetics_concept_opening_hours", label: "Opening Hours" },
    { key: "aesthetics_concept_breakfast",     label: "Breakfast opening hours" },
    { key: "aesthetics_concept_lunch",         label: "Lunch opening hours" },
    { key: "aesthetics_concept_dinner",        label: "Dinner opening hours" },
  ]},
  { header: "Ambience", fields: [
    { key: "aesthetics_ambience_description",       label: "Description" },
    { key: "aesthetics_ambience_image_url",         label: "Restaurant image" },
    { key: "aesthetics_ambience_description_theme", label: "Description & Theme" },
    { key: "aesthetics_ambience_scent",             label: "Scent" },
  ]},
  { header: "Design",   fields: [
    { key: "aesthetics_design_operating_philosophy", label: "Operating Philosophy" },
    { key: "aesthetics_design_food_direction",       label: "Food Direction" },
    { key: "aesthetics_design_drink_direction",      label: "Drink Direction" },
    { key: "aesthetics_design_style_of_service",     label: "Style of service" },
  ]},
  { header: "Lighting", fields: [
    { key: "aesthetics_lighting_image_url",   label: "Restaurant image" },
    { key: "aesthetics_lighting_bars",        label: "Bars" },
    { key: "aesthetics_lighting_live_counter", label: "Live counter" },
    { key: "aesthetics_lighting_dining_area", label: "Dining area" },
  ]},
  { header: "Entertainment", fields: [
    { key: "aesthetics_entertainment_description", label: "Description" },
  ]},
  { header: "Music",    fields: [
    { key: "aesthetics_music_breakfast", label: "Breakfast" },
    { key: "aesthetics_music_lunch",     label: "Lunch" },
    { key: "aesthetics_music_dinner",    label: "Dinner" },
  ]},
  { header: "Seating",  fields: [
    { key: "aesthetics_seating_total_no",    label: "Total No" },
    { key: "aesthetics_seating_description", label: "Description" },
  ]},
];

const OPERATIONS = [
  { header: "People",            fields: [
    { key: "ops_people_culinary_total", label: "Culinary total" },
    { key: "ops_people_service_total",  label: "Service total" },
  ]},
  { header: "Wardrobe", fields: [
    { key: "ops_wardrobe_image_url",   label: "Restaurant image" },
    { key: "ops_wardrobe_description", label: "Description" },
  ]},
  { header: "Table top",         fields: [
    { key: "ops_table_top_image_url",   label: "Restaurant image" },
    { key: "ops_table_top_napkin",      label: "Napkin" },
    { key: "ops_table_top_table_cloth", label: "Table cloth" },
    { key: "ops_table_top_detail",      label: "Detail" },
  ]},
  { header: "Operating Equipment", fields: [
    { key: "ops_operating_equipment_dining_area",  label: "Dining area",  imageUrlKey: "ops_operating_equipment_dining_area_image_url" },
    { key: "ops_operating_equipment_live_kitchen", label: "Live Kitchen", imageUrlKey: "ops_operating_equipment_live_kitchen_image_url" },
    { key: "ops_operating_equipment_kitchen",      label: "Kitchen",      imageUrlKey: "ops_operating_equipment_kitchen_image_url" },
  ]},
  { header: "Guest experience",  fields: [
    { key: "ops_guest_experience_description", label: "Description" },
  ]},
  { header: "Menu Design",       fields: [
    { key: "ops_menu_design_image_url", label: "Restaurant image" },
    { key: "ops_menu_design_food",  label: "Food",  imageUrlKey: "ops_menu_design_food_image_url" },
    { key: "ops_menu_design_drink", label: "Drink", imageUrlKey: "ops_menu_design_drink_image_url" },
  ]},
];

// Column III "Business Details" is rendered manually so we can interleave
// MonthlyTargetsCard between Outlet Demand Calendar and Target Profile in the
// exact sequence the user requested.
const BUSINESS_PRE_TARGETS = [
  { key: "biz_business_objectives",   label: "Business Objectives" },
  { key: "biz_financial_statement",   label: "Budget" },
];
const BUSINESS_MENU_ENGINEERING = [
  { key: "ops_menu_engineering_food",  label: "Food" },
  { key: "ops_menu_engineering_drink", label: "Drink" },
];
// Restaurant Occupancy Chart removed (lives inside the Outlet Demand Calendar's
// Occupancy & RevPASH tab); BUSINESS_MID kept empty so the column order is
// preserved if we re-introduce mid-column fields later.
const BUSINESS_MID = [];
// After MonthlyTargetsCard:
//   Target Profile (with editable file upload)
//   SWOT (with editable file upload)

// 4 quadrants, each with a primary field + a follow-up sub-field. Existing
// `swot_s/w/o/t` keys are preserved for the primary fields so previously
// saved data continues to render unchanged.
const SWOT = [
  {
    key: "swot_s",
    label: "Key Strengths",
    subKey: "swot_s_implications",
    subLabel: "Implications",
    accent: "bg-[#10B981]/10 border-[#10B981]/30 text-[#86EFAC] placeholder:text-[#10B981]/50",
    headerColor: "text-[#86EFAC]",
  },
  {
    key: "swot_w",
    label: "Weakness",
    subKey: "swot_w_implications",
    subLabel: "Implications",
    accent: "bg-[#EF4444]/10 border-[#EF4444]/30 text-[#FCA5A5] placeholder:text-[#EF4444]/50",
    headerColor: "text-[#FCA5A5]",
  },
  {
    key: "swot_o",
    label: "Opportunities",
    subKey: "swot_o_action",
    subLabel: "Action",
    accent: "bg-[#38BDF8]/10 border-[#38BDF8]/30 text-[#7DD3FC] placeholder:text-[#38BDF8]/50",
    headerColor: "text-[#7DD3FC]",
  },
  {
    key: "swot_t",
    label: "Challenges",
    subKey: "swot_t_impact",
    subLabel: "Impact",
    accent: "bg-[#F59E0B]/10 border-[#F59E0B]/30 text-[#FCD34D] placeholder:text-[#F59E0B]/50",
    headerColor: "text-[#FCD34D]",
  },
];

const INSIGHTS = [
  { key: "insights_current_trends", label: "Current Trends", placeholder: "Industry shifts, local trends, what to watch…" },
];

// Mandatory sub-fields under each Target Profile section. Stored as flat
// keys `biz_target_profile_<section>_<field>` for easy querying & migration.
const TARGET_PROFILE_SECTIONS = [
  {
    section: "demographics",
    label: "Demographics",
    fields: [
      "Age", "Gender", "Education", "Marital Status",
      "Professional Status", "Normal working hours in a day",
      "Type of Neighbourhood", "Names of localities they live at",
    ],
  },
  {
    section: "eating_habits",
    label: "Eating Out Habits",
    fields: [
      "Number in month", "Spend Money", "Eatout Experience",
      "Common Issues", "Payment Type", "Day of the week",
      "Occasion", "Booking Behaviour", "Eating groups",
      "Preferred meal period", "Love for Food",
    ],
  },
  {
    section: "brands_liked",
    label: "Brands they like",
    fields: [
      "Clothes", "Footwear", "Technology Gadget", "Cars they drive",
      "Watches", "Perfumes", "Shades", "Jewellery", "Bags",
    ],
  },
  {
    section: "social_behaviour",
    label: "Social Behaviour",
    fields: [
      "What motivates them", "Views about Career and Finance",
      "Memberships/Loyalty", "Social Causes",
      "Online Social behaviour", "Go to Social Networks",
      "Friends and Networks", "Vacations",
      "Last 3 places they went out to",
    ],
  },
  {
    section: "quick_notes",
    label: "Quick Notes on the Profile",
    fields: ["Note 1", "Note 2", "Note 3", "Note 4", "Note 5"],
  },
];

// snake_case-ify a free-form sub-field label so it forms a valid flat key.
const tpFieldKey = (section, fieldLabel) =>
  `biz_target_profile_${section}_${fieldLabel.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "")}`;

const TARGET_PROFILE_KEY_COUNT = TARGET_PROFILE_SECTIONS.reduce(
  (n, s) => n + s.fields.length, 0,
);

function MultiImageUploadField({
  slot, listKey, label, alt, workspaceId, locked, data, onChange, testIdPrefix,
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = React.useRef(null);

  const urls = useMemo(() => {
    try {
      const v = data[listKey];
      if (!v) return [];
      const arr = JSON.parse(v);
      return Array.isArray(arr) ? arr.filter((s) => typeof s === "string") : [];
    } catch {
      return [];
    }
  }, [data, listKey]);

  const persist = (next) => onChange(listKey, JSON.stringify(next));

  const fullSrc = (u) => (u.startsWith("http") ? u : `${BACKEND_URL}${u}`);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    setUploadError(null);
    const next = [...urls];
    try {
      for (const file of files) {
        if (!file.type.startsWith("image/")) {
          setUploadError(`Skipped non-image: ${file.name}`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post(
          `/workspaces/${workspaceId}/fnb-profile/images/${slot}`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        next.push(r.data.image_url);
      }
      persist(next);
    } catch (err) {
      setUploadError(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onRemove = (idx) => {
    if (locked) return;
    const next = urls.filter((_, i) => i !== idx);
    persist(next);
  };

  return (
    <div data-testid={`${testIdPrefix}-frame`}>
      {label && (
        <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
          {label}
        </label>
      )}
      <div className="flex flex-wrap gap-1.5 p-1.5 border border-zinc-800 rounded bg-[#0B0B0E]/40">
        {urls.map((u, i) => (
          <div
            key={`${u}_${i}`}
            className="relative group w-20 h-20 rounded overflow-hidden border border-zinc-800"
            data-testid={`${testIdPrefix}-thumb-${i}`}
          >
            <img src={fullSrc(u)} alt={`${alt} ${i + 1}`} className="w-full h-full object-cover" />
            {!locked && (
              <button
                type="button"
                onClick={() => onRemove(i)}
                data-testid={`${testIdPrefix}-thumb-remove-${i}`}
                title="Remove image"
                className="absolute top-0.5 right-0.5 bg-black/70 hover:bg-[#EF4444] text-zinc-100 rounded p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={locked || uploading}
          data-testid={`${testIdPrefix}-add-btn`}
          className="w-20 h-20 flex flex-col items-center justify-center border border-dashed border-zinc-700 hover:border-[#D4A968] rounded text-zinc-500 hover:text-[#D4A968] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          title="Add HD image(s)"
        >
          {uploading ? (
            <span className="text-[9px] font-mono uppercase tracking-widest">…</span>
          ) : (
            <>
              <span className="text-base leading-none">+</span>
              <span className="text-[9px] font-mono uppercase tracking-widest mt-0.5">
                Add HD
              </span>
            </>
          )}
        </button>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={onFiles}
        className="hidden"
        data-testid={`${testIdPrefix}-file-input`}
      />
      {uploadError && (
        <div className="mt-1 text-[11px] text-[#EF4444]" data-testid={`${testIdPrefix}-error`}>
          {uploadError}
        </div>
      )}
    </div>
  );
}

function TargetProfileCard({ data, locked, onChange, workspaceId }) {
  return (
    <div className="bg-[#141417] border border-zinc-800 rounded-lg p-3 hover:border-zinc-700 transition-colors min-w-0">
      <div className="text-[11px] font-mono uppercase tracking-widest text-[#D4A968] mb-2 pb-1.5 border-b border-zinc-800">
        Target Profile
      </div>
      <div className="space-y-4">
        {TARGET_PROFILE_SECTIONS.map((s) => (
          <div key={s.section} data-testid={`tp-section-${s.section}`}>
            <div className="text-[11px] font-semibold tracking-wide text-zinc-200 mb-2">
              {s.label}
              <span className="ml-1 text-[#EF4444]" title="Mandatory">*</span>
            </div>
            <MultiImageUploadField
              slot={`target_profile_${s.section}`}
              listKey={`biz_target_profile_${s.section}_images`}
              label=""
              alt={`${s.label} reference image`}
              workspaceId={workspaceId}
              locked={locked}
              data={data}
              onChange={onChange}
              testIdPrefix={`tp-${s.section}-images`}
            />
            <div className="space-y-1.5 mt-2">
              {s.fields.map((f) => {
                const k = tpFieldKey(s.section, f);
                return (
                  <div key={k} className="flex items-baseline gap-2">
                    <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 shrink-0 w-44 truncate">
                      {f}
                    </label>
                    <input
                      type="text"
                      value={data[k] || ""}
                      onChange={(e) => onChange(k, e.target.value)}
                      disabled={locked}
                      data-testid={`field-${k}`}
                      placeholder="—"
                      className="flex-1 min-w-0 bg-transparent border-b border-zinc-800 focus:border-[#D4A968] py-0.5 text-sm text-white outline-none transition-colors disabled:opacity-60 disabled:cursor-not-allowed placeholder:text-zinc-700"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const CRITICAL_ISSUES = "critical_issues";

// Section header colors mirror the source HTML accents (blue / green / orange)
// against Vorion's dark backdrop.
function ColumnHeader({ children, color, num }) {
  return (
    <div className="flex items-center gap-2 mb-1">
      <span className={`block w-1 h-6 rounded-full ${color}`} />
      <h2 className="text-sm sm:text-base font-bold text-white tracking-tight">
        <span className="font-mono text-zinc-500 mr-2">{num}.</span>
        {children}
      </h2>
    </div>
  );
}

// Map a card header label to a backend slot id so an `*_image_url` field
// inside that card can drive a real upload.
const HEADER_TO_SLOT = {
  "Concept": "concept",
  "Ambience": "ambience",
  "Wardrobe": "wardrobe",
  "Lighting": "lighting",
  "Table top": "table_top",
  "Operating Equipment": "operating_equipment",
  "Menu Design": "menu_design",
};

function GroupCard({ header, fields, multiline, locked, onChange, data, workspaceId }) {
  const Cmp = multiline ? "textarea" : "input";
  const slot = HEADER_TO_SLOT[header];
  return (
    <div className="bg-[#141417] border border-zinc-800 rounded-lg p-3 hover:border-zinc-700 transition-colors min-w-0">
      <div className="text-[11px] font-mono uppercase tracking-widest text-[#D4A968] mb-2 pb-1.5 border-b border-zinc-800">
        {header}
      </div>
      <div className="space-y-2">
        {fields.map((f) => {
          if (f.key.endsWith("_image_url") && slot) {
            return (
              <ImageUploadField
                key={f.key}
                slot={slot}
                urlKey={f.key}
                label={f.label}
                alt={`${header} reference image`}
                workspaceId={workspaceId}
                locked={locked}
                data={data}
                onChange={onChange}
                testIdPrefix={`${slot}-image`}
              />
            );
          }
          return (
            <div key={f.key}>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-0.5">
                {f.label}
              </label>
              {f.imageUrlKey && slot && (
                <div className="mb-1.5">
                  <ImageUploadField
                    slot={slot}
                    urlKey={f.imageUrlKey}
                    label=""
                    alt={`${header} — ${f.label}`}
                    workspaceId={workspaceId}
                    locked={locked}
                    data={data}
                    onChange={onChange}
                    testIdPrefix={`${slot}-${f.key.replace(/^ops_|^aesthetics_/, "").replace(/_image_url$/, "")}-image`}
                    compact
                  />
                </div>
              )}
              <Cmp
                type={multiline ? undefined : "text"}
                rows={multiline ? 2 : undefined}
                value={data[f.key] || ""}
                onChange={(e) => onChange(f.key, e.target.value)}
                disabled={locked}
                data-testid={`field-${f.key}`}
                placeholder={`Enter ${f.label.toLowerCase()}…`}
                className={`w-full bg-transparent border-b border-zinc-800 focus:border-[#D4A968] py-1 text-sm text-white outline-none transition-colors disabled:opacity-60 disabled:cursor-not-allowed placeholder:text-zinc-600 ${multiline ? "resize-none" : ""}`}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FieldCard({ label, value, multiline, locked, onChange, testid }) {
  const Cmp = multiline ? "textarea" : "input";
  return (
    <div className="bg-[#141417] border border-zinc-800 rounded-lg p-3 hover:border-zinc-700 transition-colors min-w-0">
      <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
        {label}
      </label>
      <Cmp
        type={multiline ? undefined : "text"}
        rows={multiline ? 2 : undefined}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={locked}
        data-testid={testid}
        placeholder={`Enter ${label.toLowerCase()}…`}
        className={`w-full bg-transparent border-b border-zinc-800 focus:border-[#D4A968] py-1 text-sm text-white outline-none transition-colors disabled:opacity-60 disabled:cursor-not-allowed placeholder:text-zinc-600 ${multiline ? "resize-none" : ""}`}
      />
    </div>
  );
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Reusable image-upload field for an F&B profile slot. Stores the URL under
 * `data[urlKey]` via onChange. `slot` matches the backend allow-list (e.g.
 * "concept", "ambience"). Hover the preview to Replace/Remove.
 */
function ImageUploadField({
  slot, urlKey, label, alt, workspaceId, locked, data, onChange, testIdPrefix, compact = false,
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const imageUrl = data[urlKey] || "";
  const fullImageSrc = imageUrl
    ? (imageUrl.startsWith("http") ? imageUrl : `${BACKEND_URL}${imageUrl}`)
    : "";
  const fileInputRef = React.useRef(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting same file
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setUploadError("Only image files are allowed");
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(
        `/workspaces/${workspaceId}/fnb-profile/images/${slot}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      onChange(urlKey, r.data.image_url);
    } catch (err) {
      setUploadError(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onRemove = () => {
    if (!window.confirm(`Remove the ${slot} image?`)) return;
    onChange(urlKey, "");
  };

  return (
    <div>
      {label && (
        <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
          {label}
        </label>
      )}
      {imageUrl ? (
        <div
          className="relative group rounded overflow-hidden border border-zinc-800"
          data-testid={`${testIdPrefix}-preview`}
        >
          <img src={fullImageSrc} alt={alt} className={`w-full ${compact ? "h-28" : "h-44"} object-cover`} />
          {!locked && (
            <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid={`${testIdPrefix}-replace-btn`}
                className="text-[10px] font-mono uppercase tracking-widest bg-black/70 hover:bg-black text-zinc-100 border border-zinc-700 rounded px-1.5 py-0.5 disabled:opacity-50"
              >
                Replace
              </button>
              <button
                type="button"
                onClick={onRemove}
                disabled={uploading}
                data-testid={`${testIdPrefix}-remove-btn`}
                className="text-[10px] font-mono uppercase tracking-widest bg-black/70 hover:bg-[#EF4444]/80 text-zinc-100 border border-zinc-700 rounded px-1.5 py-0.5 disabled:opacity-50"
              >
                <X className="w-3 h-3 inline -mt-0.5" />
              </button>
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={locked || uploading}
          data-testid={`${testIdPrefix}-upload-btn`}
          className={`w-full flex flex-col items-center justify-center gap-1 border border-dashed border-zinc-700 hover:border-[#D4A968] rounded text-zinc-400 hover:text-[#D4A968] transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${compact ? "p-2" : "p-5 gap-1.5"}`}
        >
          {uploading ? (
            <span className="text-xs font-mono uppercase tracking-widest">Uploading…</span>
          ) : (
            <>
              <Upload className={compact ? "w-3.5 h-3.5" : "w-5 h-5"} />
              <span className={`font-mono uppercase tracking-widest ${compact ? "text-[10px]" : "text-xs"}`}>
                {compact ? "Upload HD image" : "Upload high-quality image"}
              </span>
              {!compact && (
                <span className="text-[10px] text-zinc-500">JPG · PNG · WEBP · HEIC · ≤ 15 MB</span>
              )}
            </>
          )}
        </button>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={onFile}
        className="hidden"
        data-testid={`${testIdPrefix}-file-input`}
      />
      {uploadError && (
        <div className="mt-1 text-[11px] text-[#EF4444]" data-testid={`${testIdPrefix}-error`}>
          {uploadError}
        </div>
      )}
    </div>
  );
}

function DescriptionTextarea({ valueKey, label, placeholder, locked, data, onChange }) {
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
        {label}
      </label>
      <textarea
        value={data[valueKey] || ""}
        onChange={(e) => onChange(valueKey, e.target.value)}
        disabled={locked}
        data-testid={`field-${valueKey}`}
        placeholder={placeholder}
        rows={4}
        className="w-full bg-[#0B0B0E] border border-zinc-800 focus:border-[#D4A968] rounded p-2 text-sm text-white outline-none transition-colors disabled:opacity-60 disabled:cursor-not-allowed placeholder:text-zinc-600 resize-y leading-snug"
      />
    </div>
  );
}

function buildSwotKeys(prefix) {
  // For the main outlet prefix is "" so keys remain swot_s/w/o/t etc.
  // For additional outlets prefix is e.g. "outlet_abc_" → swot_outlet_abc_s etc.
  return SWOT.map((q) => ({
    ...q,
    key:    `swot_${prefix}${q.key.replace(/^swot_/, "")}`,
    subKey: `swot_${prefix}${q.subKey.replace(/^swot_/, "")}`,
  }));
}

function SwotBlock({
  outletId, // "" for main outlet, or "outlet_<id>_" prefix
  outletNameKey,
  defaultName,
  data,
  locked,
  onChange,
  onRemove,
  testIdSuffix,
}) {
  const fields = buildSwotKeys(outletId);
  const showRemove = !!onRemove;
  const nameVal = data[outletNameKey] ?? "";

  return (
    <div
      className="bg-[#141417] border border-zinc-800 rounded-lg p-3 min-w-0"
      data-testid={`swot-grid${testIdSuffix ? `-${testIdSuffix}` : ""}`}
    >
      <div className="flex items-center gap-2 mb-2 pb-1.5 border-b border-zinc-800">
        <span className="text-[11px] font-mono uppercase tracking-widest text-[#D4A968] shrink-0">
          SWOT
        </span>
        <span className="text-zinc-600 text-xs">·</span>
        <input
          type="text"
          value={nameVal}
          onChange={(e) => onChange(outletNameKey, e.target.value)}
          disabled={locked}
          data-testid={`swot-outlet-name${testIdSuffix ? `-${testIdSuffix}` : "-main"}`}
          placeholder={defaultName}
          className="flex-1 min-w-0 bg-transparent border-b border-transparent hover:border-zinc-700 focus:border-[#D4A968] text-xs text-zinc-200 outline-none transition-colors disabled:opacity-60 disabled:cursor-not-allowed placeholder:text-zinc-500 placeholder:italic px-0.5 py-0.5"
        />
        {showRemove && !locked && (
          <button
            type="button"
            onClick={onRemove}
            data-testid={`swot-outlet-remove-${testIdSuffix}`}
            title="Remove this outlet's SWOT"
            className="shrink-0 text-zinc-500 hover:text-[#EF4444] transition-colors p-0.5"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {fields.map((q) => (
          <div
            key={q.key}
            className={`rounded p-2 border ${q.accent}`}
            data-testid={`swot-cell-${q.key}`}
          >
            <div className={`text-[10px] font-mono uppercase tracking-widest mb-1 ${q.headerColor}`}>
              {q.label}
            </div>
            <textarea
              value={data[q.key] || ""}
              onChange={(e) => onChange(q.key, e.target.value)}
              disabled={locked}
              placeholder={`Add ${q.label.toLowerCase()}…`}
              data-testid={`field-${q.key}`}
              rows={7}
              className={`w-full bg-transparent text-xs outline-none resize-y min-h-[120px] leading-snug disabled:opacity-60 disabled:cursor-not-allowed ${q.accent.split(" ")[2]} ${q.accent.split(" ")[3]}`}
            />
            <div className={`text-[10px] font-mono uppercase tracking-widest mt-3 mb-1 ${q.headerColor} opacity-80`}>
              {q.subLabel}
            </div>
            <textarea
              value={data[q.subKey] || ""}
              onChange={(e) => onChange(q.subKey, e.target.value)}
              disabled={locked}
              placeholder={`Add ${q.subLabel.toLowerCase()}…`}
              data-testid={`field-${q.subKey}`}
              rows={6}
              className={`w-full bg-transparent text-xs outline-none resize-y min-h-[100px] leading-snug disabled:opacity-60 disabled:cursor-not-allowed ${q.accent.split(" ")[2]} ${q.accent.split(" ")[3]}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

const SWOT_OUTLETS_KEY = "biz_swot_outlet_ids";
function parseSwotOutlets(data) {
  try {
    const v = data?.[SWOT_OUTLETS_KEY];
    if (!v) return [];
    const arr = JSON.parse(v);
    return Array.isArray(arr) ? arr.filter((s) => typeof s === "string" && /^outlet_[a-z0-9]+$/.test(s)) : [];
  } catch {
    return [];
  }
}

function PlainHourFields({ fields, locked, data, onChange }) {
  return (
    <div className="space-y-2">
      {fields.map((f) => (
        <div key={f.key}>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-0.5">
            {f.label}
          </label>
          <input
            type="text"
            value={data[f.key] || ""}
            onChange={(e) => onChange(f.key, e.target.value)}
            disabled={locked}
            data-testid={`field-${f.key}`}
            placeholder={`Enter ${f.label.toLowerCase()}…`}
            className="w-full bg-transparent border-b border-zinc-800 focus:border-[#D4A968] py-1 text-sm text-white outline-none transition-colors disabled:opacity-60 disabled:cursor-not-allowed placeholder:text-zinc-600"
          />
        </div>
      ))}
    </div>
  );
}

function FileUploadField({ slot, urlKey, nameKey, label, workspaceId, locked, data, onChange, testIdPrefix }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = React.useRef(null);
  const url = data[urlKey] || "";
  const name = data[nameKey] || "";
  const fullUrl = url ? (url.startsWith("http") ? url : `${BACKEND_URL}${url}`) : "";

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(
        `/workspaces/${workspaceId}/fnb-profile/files/${slot}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      onChange(urlKey, r.data.file_url);
      onChange(nameKey, r.data.original_filename || file.name);
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onRemove = () => {
    if (!window.confirm("Remove this attached file?")) return;
    onChange(urlKey, "");
    onChange(nameKey, "");
  };

  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
        {label}
      </label>
      {url ? (
        <div
          className="flex items-center justify-between gap-2 bg-[#0B0B0E] border border-zinc-800 rounded px-2.5 py-1.5"
          data-testid={`${testIdPrefix}-attached`}
        >
          <a
            href={fullUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`${testIdPrefix}-link`}
            className="text-xs text-[#D4A968] hover:underline truncate flex-1"
            title={name}
          >
            📎 {name || "Attached file"}
          </a>
          {!locked && (
            <div className="flex gap-1.5 shrink-0">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid={`${testIdPrefix}-replace-btn`}
                className="text-[10px] font-mono uppercase tracking-widest text-zinc-300 hover:text-[#D4A968] border border-zinc-700 rounded px-1.5 py-0.5 disabled:opacity-50"
              >
                Replace
              </button>
              <button
                type="button"
                onClick={onRemove}
                data-testid={`${testIdPrefix}-remove-btn`}
                className="text-[10px] font-mono uppercase tracking-widest text-zinc-300 hover:text-[#EF4444] border border-zinc-700 rounded px-1.5 py-0.5"
              >
                <X className="w-3 h-3 inline -mt-0.5" />
              </button>
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={locked || uploading}
          data-testid={`${testIdPrefix}-upload-btn`}
          className="w-full flex items-center justify-center gap-2 border border-dashed border-zinc-700 hover:border-[#D4A968] rounded px-2 py-2 text-zinc-400 hover:text-[#D4A968] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Upload className="w-3.5 h-3.5" />
          <span className="text-[11px] font-mono uppercase tracking-widest">
            {uploading ? "Uploading…" : "Upload editable file"}
          </span>
          <span className="text-[9px] text-zinc-500">XLS · DOCX · PDF · ≤ 25 MB</span>
        </button>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept=".xls,.xlsx,.doc,.docx,.pdf,.csv,.txt"
        onChange={onFile}
        className="hidden"
        data-testid={`${testIdPrefix}-file-input`}
      />
      {error && (
        <div className="mt-1 text-[11px] text-[#EF4444]" data-testid={`${testIdPrefix}-error`}>
          {error}
        </div>
      )}
    </div>
  );
}

function ConceptCard({ workspaceId, locked, data, onChange }) {
  const hourFields = [
    { key: "aesthetics_concept_opening_hours", label: "Opening Hours" },
    { key: "aesthetics_concept_breakfast",     label: "Breakfast opening hours" },
    { key: "aesthetics_concept_lunch",         label: "Lunch opening hours" },
    { key: "aesthetics_concept_dinner",        label: "Dinner opening hours" },
  ];

  return (
    <div className="bg-[#141417] border border-zinc-800 rounded-lg p-3 hover:border-zinc-700 transition-colors min-w-0">
      <div className="text-[11px] font-mono uppercase tracking-widest text-[#D4A968] mb-2 pb-1.5 border-b border-zinc-800">
        Concept
      </div>
      <div className="space-y-3">
        <DescriptionTextarea
          valueKey="aesthetics_concept_description"
          label="Description"
          placeholder="Describe the restaurant's concept, story, signature feel…"
          locked={locked}
          data={data}
          onChange={onChange}
        />
        <ImageUploadField
          slot="concept"
          urlKey="aesthetics_concept_image_url"
          label="Restaurant Image"
          alt="Restaurant concept"
          workspaceId={workspaceId}
          locked={locked}
          data={data}
          onChange={onChange}
          testIdPrefix="concept-image"
        />
        <PlainHourFields fields={hourFields} locked={locked} data={data} onChange={onChange} />
      </div>
    </div>
  );
}

function AmbienceCard({ workspaceId, locked, data, onChange }) {
  const subFields = [
    { key: "aesthetics_ambience_description_theme", label: "Description & Theme" },
    { key: "aesthetics_ambience_scent",             label: "Scent" },
  ];

  return (
    <div className="bg-[#141417] border border-zinc-800 rounded-lg p-3 hover:border-zinc-700 transition-colors min-w-0">
      <div className="text-[11px] font-mono uppercase tracking-widest text-[#D4A968] mb-2 pb-1.5 border-b border-zinc-800">
        Ambience
      </div>
      <div className="space-y-3">
        <DescriptionTextarea
          valueKey="aesthetics_ambience_description"
          label="Description"
          placeholder="Describe the ambience — mood, lighting feel, soundscape, energy…"
          locked={locked}
          data={data}
          onChange={onChange}
        />
        <ImageUploadField
          slot="ambience"
          urlKey="aesthetics_ambience_image_url"
          label="Restaurant Image"
          alt="Restaurant ambience"
          workspaceId={workspaceId}
          locked={locked}
          data={data}
          onChange={onChange}
          testIdPrefix="ambience-image"
        />
        <PlainHourFields fields={subFields} locked={locked} data={data} onChange={onChange} />
      </div>
    </div>
  );
}

export default function Home() {
  const { currentWorkspaceId, current } = useWorkspace();
  const { user } = useAuth();
  const [profile, setProfile] = useState({ submitted: false, submitted_at: null, data: {} });
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!currentWorkspaceId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get(`/workspaces/${currentWorkspaceId}/fnb-profile`)
      .then((r) => {
        if (cancelled) return;
        setProfile({
          submitted: !!r.data.submitted,
          submitted_at: r.data.submitted_at || null,
          data: r.data.data || {},
        });
        setDirty(false);
      })
      .catch((e) => !cancelled && setError(e?.response?.data?.detail || "Failed to load dashboard"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [currentWorkspaceId]);

  const locked = profile.submitted;

  const setField = (key, val) => {
    if (locked) return;
    setProfile((p) => ({ ...p, data: { ...p.data, [key]: val } }));
    setDirty(true);
  };

  const persist = async (next) => {
    setSaving(true);
    setError(null);
    try {
      const r = await api.put(`/workspaces/${currentWorkspaceId}/fnb-profile`, {
        submitted: !!next.submitted,
        data: next.data,
      });
      setProfile({
        submitted: !!r.data.submitted,
        submitted_at: r.data.submitted_at || null,
        data: r.data.data || {},
      });
      setDirty(false);
      return r.data;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Save failed";
      setError(msg);
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const onSave = async () => {
    await persist({ submitted: false, data: profile.data });
    flash("Strategy saved");
  };

  const onSubmit = async () => {
    if (!window.confirm("Submit & lock the dashboard? You'll need to re-open it to edit.")) return;
    await persist({ submitted: true, data: profile.data });
    flash("Submitted & locked");
  };

  const onReopen = async () => {
    if (!window.confirm("Re-open the dashboard for editing?")) return;
    await persist({ submitted: false, data: profile.data });
    flash("Re-opened");
  };

  const onReset = async () => {
    if (!window.confirm("Reset all fields to empty? This cannot be undone.")) return;
    await persist({ submitted: false, data: {} });
    flash("Reset");
  };

  const flash = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2800);
  };

  const totalFields = useMemo(() => {
    const aestCount = AESTHETICS.reduce((s, g) => s + g.fields.length, 0);
    const opsCount = OPERATIONS.reduce((s, g) => s + g.fields.length, 0);
    const bizCount = BUSINESS_PRE_TARGETS.length + BUSINESS_MENU_ENGINEERING.length + BUSINESS_MID.length;
    // Target Profile (text + file_url + file_name) and SWOT (file_url + file_name) extras:
    const targetProfileExtra = TARGET_PROFILE_KEY_COUNT + TARGET_PROFILE_SECTIONS.length;  // 42 sub-fields + 5 image-list keys
    const swotExtra = SWOT.length * 2; // 4 primary + 4 sub textareas (file upload removed)
    const insightsCount = INSIGHTS.length + 1; // 3 + critical_issues
    return aestCount + opsCount + bizCount + targetProfileExtra + swotExtra + insightsCount + getMonthlyTargetKeys(profile.data).length;
  }, [profile.data]);
  const filledCount = useMemo(() => {
    return Object.values(profile.data || {}).filter((v) => (v || "").trim().length > 0).length;
  }, [profile.data]);
  const pct = Math.round((filledCount / totalFields) * 100);

  return (
    <div data-testid="home-page">
      <Topbar
        title={`Outlet Blueprint`}
        subtitle={`${current?.name || "Workspace"} · Live Strategy & Planning`}
        breadcrumb={`Architecture v1.0 · ${user?.name?.split(" ")[0] || "Welcome"}`}
      />

      <div className="p-4 sm:p-6 pb-24 sm:pb-28 space-y-6 max-w-7xl mx-auto">
        {/* Action bar */}
        <div
          data-testid="home-actionbar"
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-[#141417] border border-zinc-800 rounded-lg p-3"
        >
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              Completion
              {locked && (
                <span className="ml-2 inline-flex items-center gap-1 text-[#10B981]">
                  <Lock className="w-3 h-3" />
                  Locked {profile.submitted_at ? `· ${new Date(profile.submitted_at).toLocaleDateString()}` : ""}
                </span>
              )}
              {error && <span className="ml-2 text-[#EF4444]" data-testid="home-error">{error}</span>}
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <div className="flex-1 sm:w-64 sm:flex-initial h-1.5 bg-[#0B0B0E] rounded overflow-hidden">
                <div className="h-full bg-[#D4A968] transition-all" style={{ width: `${pct}%` }} />
              </div>
              <span className="text-xs font-mono text-zinc-300 tabular-nums">{filledCount}/{totalFields}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!locked && (
              <>
                <button
                  onClick={onReset}
                  disabled={saving}
                  data-testid="home-reset-btn"
                  className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white border border-zinc-800 hover:border-zinc-600 rounded px-2.5 py-1.5 disabled:opacity-50"
                >
                  <RotateCcw className="w-3 h-3" /> Reset
                </button>
                <button
                  onClick={onSave}
                  disabled={!dirty || saving}
                  data-testid="home-save-btn"
                  className="flex items-center gap-1.5 text-xs text-zinc-200 border border-zinc-700 hover:border-[#D4A968] rounded px-2.5 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Save className="w-3 h-3" /> {saving ? "Saving…" : (dirty ? "Save changes" : "Saved")}
                </button>
                <button
                  onClick={onSubmit}
                  disabled={saving}
                  data-testid="home-submit-btn"
                  className="flex items-center gap-1.5 text-xs font-semibold bg-[#D4A968] hover:bg-[#E8C285] text-black rounded px-3 py-1.5 disabled:opacity-50"
                >
                  <Check className="w-3 h-3" /> Submit & lock
                </button>
              </>
            )}
            {locked && (
              <button
                onClick={onReopen}
                disabled={saving}
                data-testid="home-reopen-btn"
                className="flex items-center gap-1.5 text-xs text-zinc-200 border border-zinc-700 hover:border-[#D4A968] rounded px-2.5 py-1.5 disabled:opacity-50"
              >
                <Pencil className="w-3 h-3" /> Re-open
              </button>
            )}
          </div>
        </div>

        {/* Three-column dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="home-columns">
          {/* I. Character & Vibe */}
          <section className="space-y-3 min-w-0" data-testid="col-aesthetics">
            <ColumnHeader num="I" color="bg-[#D4A968]">Character &amp; Vibe</ColumnHeader>
            {AESTHETICS.map((g) => {
              if (g.header === "Concept") {
                return (
                  <ConceptCard
                    key={g.header}
                    workspaceId={currentWorkspaceId}
                    data={profile.data}
                    onChange={setField}
                    locked={locked || loading}
                  />
                );
              }
              if (g.header === "Ambience") {
                return (
                  <AmbienceCard
                    key={g.header}
                    workspaceId={currentWorkspaceId}
                    data={profile.data}
                    onChange={setField}
                    locked={locked || loading}
                  />
                );
              }
              return (
                <GroupCard
                  key={g.header}
                  header={g.header}
                  fields={g.fields}
                  data={profile.data}
                  onChange={setField}
                  locked={locked || loading}
                  workspaceId={currentWorkspaceId}
                />
              );
            })}
          </section>

          {/* II. Operational Details */}
          <section className="space-y-3 min-w-0" data-testid="col-operations">
            <ColumnHeader num="II" color="bg-[#10B981]">Operational Details</ColumnHeader>
            {OPERATIONS.map((g) => (
              <GroupCard
                key={g.header}
                header={g.header}
                fields={g.fields}
                data={profile.data}
                onChange={setField}
                locked={locked || loading}
                workspaceId={currentWorkspaceId}
                multiline
              />
            ))}
          </section>

          {/* III. Business Details */}
          <section className="space-y-3 min-w-0" data-testid="col-business">
            <ColumnHeader num="III" color="bg-[#D4A968]">Business Details</ColumnHeader>

            {/* 1. Business Objectives, 2. Budget */}
            {BUSINESS_PRE_TARGETS.map((f) => (
              <FieldCard
                key={f.key}
                label={f.label}
                value={profile.data[f.key]}
                onChange={(v) => setField(f.key, v)}
                locked={locked || loading}
                testid={`field-${f.key}`}
                multiline
              />
            ))}

            {/* 3. Menu Engineering (moved from Operations) */}
            <GroupCard
              header="Menu Engineering"
              fields={BUSINESS_MENU_ENGINEERING}
              data={profile.data}
              onChange={setField}
              locked={locked || loading}
              workspaceId={currentWorkspaceId}
              multiline
            />

            {/* 4. Restaurant Occupancy Chart was removed — its data lives inside the
                Outlet Demand Calendar (Occupancy & RevPASH tab). */}

            {/* 5. Outlet Demand Calendar moved to its own full-width Section V below the 3 columns
                so the year wall calendar has room to breathe. */}

            {/* 6. Focus Revenue per Month — moved to its own full-width section above Opportunity Dashboard. */}

            {/* 7. Target Profile (mandatory sub-fields per section) */}
            <TargetProfileCard
              data={profile.data}
              locked={locked || loading}
              onChange={setField}
              workspaceId={currentWorkspaceId}
            />

            {/* SWOT moved out of the narrow Section III column into a full-width Section IV below the 3 columns. */}
          </section>
        </div>

        {/* Section IV — SWOT (full-width; multiple outlets render side-by-side, scroll horizontally if needed) */}
        <section className="min-w-0 space-y-3" data-testid="section-swot">
          <ColumnHeader num="IV" color="bg-[#10B981]">SWOT</ColumnHeader>
          {(() => {
            const swotOutlets = parseSwotOutlets(profile.data);
            const setSwotOutlets = (ids) => setField(SWOT_OUTLETS_KEY, JSON.stringify(ids));
            const onAddOutlet = () => {
              if (locked || loading) return;
              const newId = `outlet_${Date.now().toString(36)}`;
              setSwotOutlets([...swotOutlets, newId]);
            };
            const onRemoveOutlet = (id) => {
              if (locked || loading) return;
              if (!window.confirm("Remove this outlet's SWOT? Its data will be deleted.")) return;
              const prefix = `${id}_`;
              const cleanup = [
                `swot_${prefix}name`,
                ...buildSwotKeys(prefix).flatMap((q) => [q.key, q.subKey]),
              ];
              cleanup.forEach((k) => setField(k, ""));
              setSwotOutlets(swotOutlets.filter((x) => x !== id));
            };
            const totalOutlets = 1 + swotOutlets.length;
            return (
              <div className="space-y-3" data-testid="swot-section">
                <div className="overflow-x-auto -mx-1 px-1">
                  <div className="flex gap-3 items-stretch min-w-min">
                    <div
                      className="shrink-0 resize-x overflow-auto min-w-[360px] max-w-[1600px]"
                      style={{ width: `calc((100% - ${(totalOutlets - 1) * 12}px) / ${totalOutlets})` }}
                      data-testid="swot-outlet-wrapper-0"
                    >
                      <SwotBlock
                        outletId=""
                        outletNameKey="swot_main_outlet_name"
                        defaultName="Outlet 1"
                        data={profile.data}
                        locked={locked || loading}
                        onChange={setField}
                      />
                    </div>
                    {swotOutlets.map((id, i) => (
                      <div
                        key={id}
                        className="shrink-0 resize-x overflow-auto min-w-[360px] max-w-[1600px]"
                        style={{ width: `calc((100% - ${(totalOutlets - 1) * 12}px) / ${totalOutlets})` }}
                        data-testid={`swot-outlet-wrapper-${i + 1}`}
                      >
                        <SwotBlock
                          outletId={`${id}_`}
                          outletNameKey={`swot_${id}_name`}
                          defaultName="New outlet"
                          data={profile.data}
                          locked={locked || loading}
                          onChange={setField}
                          onRemove={() => onRemoveOutlet(id)}
                          testIdSuffix={id}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 -mt-1">
                  Tip · drag the bottom-right corner of any SWOT card to resize it horizontally and pack more outlets on screen.
                </div>
                <button
                  type="button"
                  onClick={onAddOutlet}
                  disabled={locked || loading}
                  data-testid="swot-add-outlet-btn"
                  className="w-full flex items-center justify-center gap-2 border border-dashed border-zinc-700 hover:border-[#D4A968] rounded px-3 py-2 text-zinc-300 hover:text-[#D4A968] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Add SWOT for another outlet"
                >
                  <span className="text-base leading-none">+</span>
                  <span className="text-[11px] font-mono uppercase tracking-widest">
                    Add another outlet
                  </span>
                </button>
              </div>
            );
          })()}
        </section>

        {/* Section V — Outlet Demand Calendar (full-width so the year wall calendar can breathe) */}
        <section className="min-w-0 space-y-3" data-testid="section-outlet-demand-calendar">
          <ColumnHeader num="V" color="bg-[#D4A968]">Outlet Demand Calendar</ColumnHeader>
          <OutletDemandCalendar
            workspaceId={currentWorkspaceId}
            data={profile.data}
            locked={locked || loading}
            onChange={setField}
          />
        </section>

        {/* Section V.b — Focus Revenue per Month (full-width, sits between Demand Calendar and Opportunity) */}
        <section className="min-w-0 space-y-3" data-testid="section-focus-revenue">
          <ColumnHeader num="VI" color="bg-[#D4A968]">Focus Revenue per Month</ColumnHeader>
          <MonthlyTargetsCard
            data={profile.data}
            locked={locked || loading}
            onChange={setField}
          />
        </section>

        {/* Section VII — Opportunity Dashboard (Annual Gap Engine pulled from Demand Calendar)
            Premium-only. Basic users don't see this section at all. */}
        {(user?.tier === "premium" || user?.is_global_admin) && (
          <section className="min-w-0 space-y-3" data-testid="section-opportunity-dashboard">
            <ColumnHeader num="VII" color="bg-[#0B3D2E]">Opportunity Dashboard</ColumnHeader>
            <OpportunityDashboard
              workspaceId={currentWorkspaceId}
              data={profile.data}
              locked={locked || loading}
              onChange={setField}
            />
          </section>
        )}


        {/* Section VIII — Trends & Issues (Current Trends / Critical Issues) */}
        <section className="min-w-0" data-testid="section-insights">
          <ColumnHeader num="VIII" color="bg-[#10B981]">Trends &amp; Issues</ColumnHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {INSIGHTS.map((f) => (
              <div
                key={f.key}
                className="bg-[#141417] border border-zinc-800 rounded-lg p-3 hover:border-zinc-700 transition-colors min-w-0"
                data-testid={`insights-card-${f.key}`}
              >
                <div className="text-[11px] font-mono uppercase tracking-widest text-[#D4A968] mb-2 pb-1.5 border-b border-zinc-800">
                  {f.label}
                </div>
                <textarea
                  value={profile.data[f.key] || ""}
                  onChange={(e) => setField(f.key, e.target.value)}
                  disabled={locked || loading}
                  placeholder={f.placeholder}
                  data-testid={`field-${f.key}`}
                  rows={5}
                  className="w-full bg-[#0B0B0E] border border-zinc-800 focus:border-[#D4A968] rounded p-2 text-sm text-white outline-none transition-colors disabled:opacity-60 disabled:cursor-not-allowed placeholder:text-zinc-600 resize-y leading-snug"
                />
              </div>
            ))}
            {/* Critical Issues kept with red alert styling */}
            <div className="bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-lg p-3 min-w-0" data-testid="critical-issues-box">
              <div className="flex items-center gap-1.5 mb-2 pb-1.5 border-b border-[#EF4444]/30">
                <AlertTriangle className="w-3.5 h-3.5 text-[#EF4444]" />
                <span className="text-[11px] font-mono uppercase tracking-widest text-[#FCA5A5]">
                  Critical Issues
                </span>
              </div>
              <textarea
                value={profile.data[CRITICAL_ISSUES] || ""}
                onChange={(e) => setField(CRITICAL_ISSUES, e.target.value)}
                disabled={locked || loading}
                placeholder="List bottlenecks, blockers, and red flags here…"
                data-testid={`field-${CRITICAL_ISSUES}`}
                rows={5}
                className="w-full bg-transparent text-sm text-[#FECACA] placeholder:text-[#EF4444]/50 outline-none resize-y disabled:opacity-60 disabled:cursor-not-allowed"
              />
            </div>
          </div>
        </section>
      </div>

      {/* Toast */}
      {toast && (
        <div
          data-testid="home-toast"
          className="fixed bottom-4 right-4 bg-[#141417] border border-[#D4A968]/40 text-[#D4A968] text-xs font-mono uppercase tracking-widest rounded px-3 py-2 shadow-2xl flex items-center gap-2 z-40"
        >
          <Sparkles className="w-3.5 h-3.5" /> {toast}
        </div>
      )}
    </div>
  );
}
